import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ClipboardList, Trash2, ArrowUp, ArrowDown, RefreshCw, X, GitBranchPlus } from 'lucide-react';
import { assessmentService, companyService } from '../services';
import TemplatePicker from '../components/TemplatePicker';
import DeleteAuditModal from '../components/DeleteAuditModal';
import Layout from '../components/Layout';
import { Card, Badge, Button } from '../components/ds';
import { useActiveAudit } from '../contexts/ActiveAuditContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import { getRisk, fetchPosture } from '../utils/posture';

const FONT      = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const BRAND     = '#1E4FBE';

const statusLabel = (status) => status === 'completed' ? 'Abgeschlossen' : 'In Bearbeitung';

const formatDate = (d) => new Date(d).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });

// ── Sortable column header ──────────────────────────────────────────────
const SortHeader = ({ label, col, sortBy, sortDir, onSort }) => {
  const active = sortBy === col;
  return (
    <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#8A93A3' }}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        onClick={() => onSort(col)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
          cursor: 'pointer', padding: 0, fontFamily: FONT, fontSize: 12, fontWeight: 600,
          color: active ? BRAND : '#8A93A3',
        }}
      >
        {label}
        {active && (sortDir === 'asc' ? <ArrowUp width={12} height={12} /> : <ArrowDown width={12} height={12} />)}
      </button>
    </th>
  );
};

// ── Audit-Zeile als Karte (mobil, ersetzt die Tabellenzeile) ────────────
const AuditRowCard = ({ assessment, posture, risk, isActive, onOpen, onFollowUp, onDelete, followingUp }) => (
  <div
    onClick={onOpen}
    style={{
      padding: '14px 16px', borderBottom: '1px solid #EDEFF3', cursor: 'pointer',
      display: 'flex', flexDirection: 'column', gap: 8,
      background: isActive ? '#F2F6FE' : 'transparent',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#2C3646' }}>
        {formatDate(assessment.createdAt)}
        {isActive && <Badge tone="info">Aktiv</Badge>}
      </div>
      <Badge tone={assessment.status === 'completed' ? 'secure' : 'neutral'}>{statusLabel(assessment.status)}</Badge>
    </div>
    {posture ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: FONT_MONO, fontWeight: 700, color: '#101623' }}>{posture.overallScore}</span>
        <Badge tone={risk.tone}>{risk.label}</Badge>
        <span style={{ color: '#8A93A3', fontSize: 12 }}>
          {posture.totalFindings} offene Befunde · {posture.answeredCount}/{posture.totalControls} Kontrollen
        </span>
      </div>
    ) : (
      <span style={{ fontSize: 12, color: '#8A93A3' }}>Noch keine Auswertung</span>
    )}
    <div style={{ fontSize: 12, color: '#667085' }}>{assessment.description || assessment.templateId || '–'}</div>
    <div style={{ display: 'flex', gap: 8, marginTop: 4 }} onClick={e => e.stopPropagation()}>
      {assessment.status === 'completed' && (
        <button
          onClick={onFollowUp}
          disabled={followingUp}
          title="Folge-Audit starten"
          aria-label={`Folge-Audit zu Audit vom ${formatDate(assessment.createdAt)} starten`}
          style={{
            minHeight: 44, minWidth: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: '1px solid #D7DCE4', borderRadius: 8, color: '#2C3646',
            cursor: followingUp ? 'wait' : 'pointer',
          }}
        >
          <GitBranchPlus width={16} height={16} />
        </button>
      )}
      <button
        onClick={onDelete}
        title="Audit löschen"
        aria-label={`Audit vom ${formatDate(assessment.createdAt)} löschen`}
        style={{
          minHeight: 44, minWidth: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'none', border: '1px solid #D7DCE4', borderRadius: 8, color: '#C22B31', cursor: 'pointer',
        }}
      >
        <Trash2 width={16} height={16} />
      </button>
    </div>
  </div>
);

const AuditsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeAuditId, setActiveAuditId } = useActiveAudit();
  const isMobile = useIsMobile();

  const [notice,             setNotice]              = useState(location.state?.notice || null);
  const [company,            setCompany]            = useState(null);
  const [assessments,        setAssessments]         = useState([]);
  const [postures,           setPostures]            = useState({}); // id → posture | null
  const [loading,            setLoading]             = useState(true);
  const [error,              setError]               = useState(null);
  const [sortBy,             setSortBy]              = useState('date'); // 'date' | 'score'
  const [sortDir,            setSortDir]             = useState('desc');
  const [showTemplatePicker, setShowTemplatePicker]  = useState(false);
  const [deleteTarget,       setDeleteTarget]        = useState(null);
  const [deleting,           setDeleting]            = useState(false);
  const [followingUpId,      setFollowingUpId]       = useState(null);

  // Zugangs-Gating läuft zentral über SubscriptionGate (App.js) — diese Seite wird
  // nur bei aktivem Abo gerendert (früher Clerks has({ plan }), nicht mehr gültig).

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cRes = await companyService.getMyCompany();
      setCompany(cRes.data);
      const aRes = await assessmentService.getAssessmentsByCompany(cRes.data.id);
      setAssessments(aRes.data);

      const entries = await Promise.all(aRes.data.map(async (a) => {
        try { return [a.id, await fetchPosture(a)]; }
        catch { return [a.id, null]; }
      }));
      setPostures(Object.fromEntries(entries));
    } catch (err) {
      if (err.response?.status === 404) {
        navigate('/dashboard', { replace: true });
        return;
      }
      setError('Audits konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const handleTemplateSelected = async (template) => {
    setShowTemplatePicker(false);
    try {
      const res = await assessmentService.createAssessment({
        companyId: company.id,
        title: template.name,
        description: template.standard || '',
        templateId: template.id,
      });
      navigate(`/assessment/${res.data.id}`);
    } catch (err) { console.error(err); }
  };

  const handleRowOpen = (assessment) => {
    // Abgeschlossene Audits setzen sich aktiv und zeigen die Übersicht;
    // laufende Audits führen zurück in den Fragebogen-Flow.
    if (assessment.status === 'completed') navigate(`/audits/${assessment.id}`);
    else navigate(`/assessment/${assessment.id}`);
  };

  const handleFollowUp = async (assessment) => {
    setFollowingUpId(assessment.id);
    try {
      const res = await assessmentService.createFollowUp(assessment.id);
      setActiveAuditId(res.data.id);
      navigate(`/assessment/${res.data.id}`);
    } catch (err) {
      console.error(err);
    } finally {
      setFollowingUpId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await assessmentService.deleteAssessment(deleteTarget.id);
      const wasActive = deleteTarget.id === activeAuditId;
      const remaining = assessments.filter(a => a.id !== deleteTarget.id);
      setDeleteTarget(null);
      if (wasActive) {
        const stillDone = remaining
          .filter(a => a.status === 'completed')
          .sort((a, b) => new Date(b.completedAt || b.updatedAt || b.createdAt) - new Date(a.completedAt || a.updatedAt || a.createdAt));
        setActiveAuditId(stillDone[0]?.id || null);
      }
      await load();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const sorted = [...assessments].sort((a, b) => {
    let av, bv;
    if (sortBy === 'score') {
      av = postures[a.id]?.overallScore ?? -1;
      bv = postures[b.id]?.overallScore ?? -1;
    } else {
      av = new Date(a.createdAt).getTime();
      bv = new Date(b.createdAt).getTime();
    }
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  // ── Loading ──
  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', color: '#8A93A3', fontSize: 14, fontFamily: FONT }}>
        Lädt…
      </div>
    </Layout>
  );

  // ── Error ──
  if (error) return (
    <Layout>
      <div style={{ padding: '36px 40px', fontFamily: FONT }}>
        <Card padding={0}>
          <div style={{ padding: '48px 40px', textAlign: 'center' }}>
            <p style={{ margin: '0 0 18px', fontSize: 14, color: '#C22B31' }}>{error}</p>
            <Button size="md" onClick={load} leadingIcon={<RefreshCw width={14} height={14} />}>
              Erneut versuchen
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );

  const noticeBanner = notice && (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      background: '#FDF6EA', border: '1px solid #FBEBD3', borderRadius: 10,
      padding: '11px 16px', marginBottom: 20, fontSize: 13, color: '#8A4B0A',
    }}>
      <span>{notice}</span>
      <button onClick={() => setNotice(null)} aria-label="Hinweis schließen" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8A4B0A' }}>
        <X width={14} height={14} />
      </button>
    </div>
  );

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#101623', letterSpacing: '-0.011em' }}>
          Audits
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8A93A3', fontFamily: FONT_MONO }}>
          {company?.name} · {assessments.length} Audit{assessments.length !== 1 ? 's' : ''}
        </p>
      </div>
      {company && (
        <Button size="md" onClick={() => setShowTemplatePicker(true)}>Neues Audit</Button>
      )}
    </div>
  );

  // ── Empty state ──
  if (assessments.length === 0) return (
    <Layout>
      <div style={{ padding: '36px 40px', fontFamily: FONT }}>
        {noticeBanner}
        {header}
        <Card padding={0}>
          <div style={{ padding: '64px 40px', textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16, background: '#F2F6FE', color: BRAND,
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
            }}>
              <ClipboardList width={30} height={30} />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#101623' }}>
              Noch keine Audits
            </h2>
            <p style={{ color: '#667085', fontSize: 14, margin: '0 0 24px', lineHeight: 1.7, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
              Führen Sie Ihr erstes IT-Assessment durch, um Ihren Sicherheitsstatus zu visualisieren.
            </p>
            {company && (
              <Button size="md" onClick={() => setShowTemplatePicker(true)}>Erstes Audit starten</Button>
            )}
          </div>
        </Card>
      </div>
      {showTemplatePicker && <TemplatePicker onSelect={handleTemplateSelected} onCancel={() => setShowTemplatePicker(false)} />}
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: '36px 40px', fontFamily: FONT }}>
        {noticeBanner}
        {header}

        <Card padding={0}>
          {isMobile ? (
            <div>
              {sorted.map(a => {
                const p = postures[a.id];
                const risk = p ? getRisk(p.overallScore) : null;
                return (
                  <AuditRowCard
                    key={a.id}
                    assessment={a} posture={p} risk={risk}
                    isActive={a.id === activeAuditId}
                    onOpen={() => handleRowOpen(a)}
                    onFollowUp={() => handleFollowUp(a)}
                    onDelete={() => setDeleteTarget(a)}
                    followingUp={followingUpId === a.id}
                  />
                );
              })}
            </div>
          ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #D7DCE4' }}>
                  <SortHeader label="Erstellt am" col="date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#8A93A3' }}>Status</th>
                  <SortHeader label="Gesamt-Score" col="score" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#8A93A3' }}>Offene Befunde</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#8A93A3' }}>Geprüfte Kontrollen</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#8A93A3' }}>Framework</th>
                  <th style={{ textAlign: 'right', padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#8A93A3' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(a => {
                  const p = postures[a.id];
                  const risk = p ? getRisk(p.overallScore) : null;
                  const isActive = a.id === activeAuditId;
                  return (
                    <tr
                      key={a.id}
                      onClick={() => handleRowOpen(a)}
                      style={{
                        borderBottom: '1px solid #EDEFF3', cursor: 'pointer',
                        background: isActive ? '#F2F6FE' : 'transparent',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#FAFBFC'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ padding: '13px 16px', fontSize: 13, color: '#2C3646' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {formatDate(a.createdAt)}
                          {isActive && <Badge tone="info">Aktiv</Badge>}
                        </div>
                      </td>
                      <td style={{ padding: '13px 16px', fontSize: 13 }}>
                        <Badge tone={a.status === 'completed' ? 'secure' : 'neutral'}>{statusLabel(a.status)}</Badge>
                      </td>
                      <td style={{ padding: '13px 16px', fontSize: 13 }}>
                        {p ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontFamily: FONT_MONO, fontWeight: 700, color: '#101623' }}>{p.overallScore}</span>
                            <Badge tone={risk.tone}>{risk.label}</Badge>
                          </div>
                        ) : '–'}
                      </td>
                      <td style={{ padding: '13px 16px', fontSize: 13, color: '#2C3646' }}>
                        {p ? p.totalFindings : '–'}
                      </td>
                      <td style={{ padding: '13px 16px', fontSize: 13, color: '#2C3646' }}>
                        {p ? `${p.answeredCount}/${p.totalControls}` : '–'}
                      </td>
                      <td style={{ padding: '13px 16px', fontSize: 13, color: '#667085' }}>
                        {a.description || a.templateId || '–'}
                      </td>
                      <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          {a.status === 'completed' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleFollowUp(a); }}
                              disabled={followingUpId === a.id}
                              title="Folge-Audit starten"
                              aria-label={`Folge-Audit zu Audit vom ${formatDate(a.createdAt)} starten`}
                              style={{
                                background: 'none', border: 'none', cursor: followingUpId === a.id ? 'wait' : 'pointer',
                                color: '#B6BDC9', padding: 6, borderRadius: 6, display: 'inline-flex',
                              }}
                              onMouseEnter={e => e.currentTarget.style.color = BRAND}
                              onMouseLeave={e => e.currentTarget.style.color = '#B6BDC9'}
                            >
                              <GitBranchPlus width={15} height={15} />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(a); }}
                            title="Audit löschen"
                            aria-label={`Audit vom ${formatDate(a.createdAt)} löschen`}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: '#B6BDC9', padding: 6, borderRadius: 6, display: 'inline-flex',
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = '#C22B31'}
                            onMouseLeave={e => e.currentTarget.style.color = '#B6BDC9'}
                          >
                            <Trash2 width={15} height={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </Card>
      </div>

      {showTemplatePicker && <TemplatePicker onSelect={handleTemplateSelected} onCancel={() => setShowTemplatePicker(false)} />}
      {deleteTarget && (
        <DeleteAuditModal
          target={deleteTarget}
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </Layout>
  );
};

export default AuditsPage;
